"use client";

import { useMemo, useState } from "react";
import { InsightCard } from "@/components/insights/InsightCard";
import { InsightsSubscribeForm } from "@/components/insights/InsightsSubscribeForm";
import type { InsightArticle, InsightTag } from "@/lib/insights/types";
import { useTranslation } from "@/lib/i18n";
import { makeTr } from "@/lib/tools-i18n";
import { INSIGHTS_BG, tagLabel } from "@/lib/tools-i18n/insights";

interface InsightsHubClientProps {
  articles: InsightArticle[];
  tagsUsed: Array<{ tag: InsightTag; count: number }>;
}

export function InsightsHubClient({ articles, tagsUsed }: InsightsHubClientProps) {
  const { language } = useTranslation();
  const tr = makeTr(language, INSIGHTS_BG);
  const [activeTag, setActiveTag] = useState<InsightTag | null>(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let list = articles;
    if (activeTag) {
      list = list.filter((a) => a.frontmatter.tags.includes(activeTag));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.frontmatter.title.toLowerCase().includes(q) ||
          a.frontmatter.excerpt.toLowerCase().includes(q),
      );
    }
    return list;
  }, [articles, activeTag, query]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12 sm:pt-32 sm:pb-16">
      {/* Header */}
      <header className="mb-10 max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
          {tr("Industry Analysis · Weekly")}
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
          {tr("Insights")}
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {tr("Weekly analysis on DWDM, IP transit, submarine cables, peering, and telecom regulation — written for carrier, ISP and enterprise network decision-makers. Curated by the Sofia Connect team.")}
          {language === "bg" ? ` ${tr("Articles are published in English.")}` : null}
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          <a
            href="/insights/feed.xml"
            className="text-primary-text hover:underline inline-flex items-center gap-1"
          >
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16" />
              <circle cx="5" cy="19" r="1.5" fill="currentColor" />
            </svg>
            {tr("Subscribe via RSS")}
          </a>
        </p>
      </header>

      {/* Filters */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            aria-pressed={activeTag === null}
            className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              activeTag === null
                ? "bg-primary text-white"
                : "bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
          >
            {tr("All")} ({articles.length})
          </button>
          {tagsUsed.map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              aria-pressed={activeTag === tag}
              className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeTag === tag
                  ? "bg-primary text-white"
                  : "bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              }`}
            >
              {tagLabel(tag, language)} ({count})
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="insights-search">
          {tr("Search insights")}
        </label>
        <input
          id="insights-search"
          type="search"
          placeholder={tr("Search insights…")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full sm:w-64 px-3 py-2 rounded-md bg-surface border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/40"
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {tr("No insights match these filters yet.")}{" "}
          <button
            type="button"
            onClick={() => {
              setActiveTag(null);
              setQuery("");
            }}
            className="text-primary-light hover:underline"
          >
            {tr("Clear filters")}
          </button>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <InsightCard key={a.frontmatter.slug} article={a} />
          ))}
        </div>
      )}

      {/* Subscribe CTA */}
      <section className="mt-16">
        <InsightsSubscribeForm variant="card" />
      </section>
    </div>
  );
}
